import React from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { LineChart, Line, ResponsiveContainer } from 'recharts';

// ── Tailwind class → CSS value maps (so callers can still pass TW strings) ──
const TW_BG = {
  'bg-violet-100':  { light: 'rgba(139,92,246,0.12)',  dark: 'rgba(139,92,246,0.20)' },
  'bg-emerald-100': { light: 'rgba(16,185,129,0.12)',  dark: 'rgba(16,185,129,0.20)' },
  'bg-cyan-100':    { light: 'rgba(6,182,212,0.12)',   dark: 'rgba(6,182,212,0.20)'  },
  'bg-rose-100':    { light: 'rgba(244,63,94,0.12)',   dark: 'rgba(244,63,94,0.20)'  },
  'bg-amber-100':   { light: 'rgba(245,158,11,0.12)',  dark: 'rgba(245,158,11,0.20)' },
  'bg-blue-100':    { light: 'rgba(59,130,246,0.12)',  dark: 'rgba(59,130,246,0.20)' },
};
const TW_COLOR = {
  'text-violet-600':  '#8b5cf6',
  'text-emerald-600': '#10b981',
  'text-cyan-600':    '#06b6d4',
  'text-rose-600':    '#f43f5e',
  'text-amber-600':   '#d97706',
  'text-blue-600':    '#3b82f6',
};

const resolveBg    = (val, isDark) => TW_BG[val]?.[isDark ? 'dark' : 'light'] ?? val ?? null;
const resolveColor = (val)         => TW_COLOR[val] ?? val ?? null;

// ── Sparkline ────────────────────────────────────────────────────────────────
const SparkLine = ({ data }) => {
  const chartData = data.map((v, i) => ({ v, i }));
  return (
    <ResponsiveContainer width="100%" height={44}>
      <LineChart data={chartData}>
        <Line type="monotone" dataKey="v" stroke="#F87217" strokeWidth={2} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
};

// ── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({
  label,
  value,
  change,
  icon: Icon,
  iconBg,
  iconColor,
  isCurrency = false,
  isWarning  = false,
  isAlert    = false,
  isLive     = false,
  spark      = null,
  isDark     = false,
}) => {
  const isPositive = change >= 0;

  const formatValue = (v) => {
    if (isCurrency) {
      if (v >= 1000) return `$${(v / 1000).toFixed(1)}k`;
      return `$${v}`;
    }
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000)     return `${(v / 1_000).toFixed(1)}k`;
    return v.toLocaleString();
  };

  // ── Card background & border ──
  let cardBg     = isDark ? '#0f1729' : '#ffffff';
  let cardBorder = isDark ? '#1e2d52' : '#e2e8f0';

  // ── Icon area defaults ──
  let iconBgVal    = resolveBg(iconBg, isDark)    ?? (isDark ? 'rgba(248,114,23,0.14)' : 'rgba(248,114,23,0.10)');
  let iconColorVal = resolveColor(iconColor)       ?? '#F87217';

  if (isWarning) {
    cardBg       = isDark ? 'rgba(234,179,8,0.06)'  : '#fefce8';
    cardBorder   = isDark ? 'rgba(234,179,8,0.30)'  : '#fde68a';
    iconBgVal    = isDark ? 'rgba(234,179,8,0.14)'  : '#fef9c3';
    iconColorVal = '#ca8a04';
  }
  if (isAlert) {
    cardBg       = isDark ? 'rgba(239,68,68,0.06)'  : '#fff1f2';
    cardBorder   = isDark ? 'rgba(239,68,68,0.30)'  : '#fecdd3';
    iconBgVal    = isDark ? 'rgba(239,68,68,0.14)'  : '#ffe4e6';
    iconColorVal = '#dc2626';
  }

  // ── Trend chip colors ──
  const trendColor = isPositive ? '#10b981' : '#ef4444';
  const trendBg    = isPositive
    ? (isDark ? 'rgba(16,185,129,0.14)' : 'rgba(16,185,129,0.10)')
    : (isDark ? 'rgba(239,68,68,0.14)'  : 'rgba(239,68,68,0.10)');

  // ── LIVE badge colors ──
  const liveBg     = isDark ? 'rgba(16,185,129,0.14)' : 'rgba(16,185,129,0.08)';
  const liveBorder = isDark ? 'rgba(16,185,129,0.35)' : 'rgba(16,185,129,0.30)';
  const liveText   = isDark ? '#34d399' : '#059669';

  return (
    <div
      className="rounded-2xl p-5 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
      style={{
        background:  cardBg,
        border:      `1px solid ${cardBorder}`,
        boxShadow:   isDark ? '0 1px 8px rgba(0,0,0,0.18)' : '0 1px 8px rgba(0,0,0,0.06)',
      }}
    >
      <div className="flex items-start justify-between mb-3">
        {/* Icon */}
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ background: iconBgVal }}
        >
          {Icon && <Icon className="w-5 h-5" style={{ color: iconColorVal }} />}
        </div>

        {/* LIVE badge */}
        {isLive && (
          <span
            className="flex items-center gap-1.5 text-[10px] font-semibold px-2 py-0.5 rounded-full"
            style={{ color: liveText, background: liveBg, border: `1px solid ${liveBorder}` }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            LIVE
          </span>
        )}
      </div>

      {/* Label */}
      <p className="text-xs font-medium mb-1" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>
        {label}
      </p>

      {/* Value */}
      <p
        className="text-2xl font-bold font-['Outfit'] mb-2"
        style={{ color: isDark ? '#f0f6fc' : '#0f172a' }}
      >
        {formatValue(value)}
      </p>

      {/* Sparkline */}
      {spark && (
        <div className="mb-2 -mx-1">
          <SparkLine data={spark} />
        </div>
      )}

      {/* Change chip */}
      {change !== undefined && (
        <div className="flex items-center gap-1.5 flex-wrap">
          {isPositive
            ? <TrendingUp  className="w-3.5 h-3.5" style={{ color: trendColor }} />
            : <TrendingDown className="w-3.5 h-3.5" style={{ color: trendColor }} />
          }
          <span
            className="text-xs font-semibold px-1.5 py-0.5 rounded-full"
            style={{ color: trendColor, background: trendBg }}
          >
            {isPositive ? '+' : ''}{change}%
          </span>
          <span className="text-xs" style={{ color: isDark ? '#64748b' : '#94a3b8' }}>
            vs last month
          </span>
        </div>
      )}
    </div>
  );
};

export default StatCard;